import React, {useCallback, useContext, useEffect, useMemo, useRef} from 'react';
import {arrayOf, bool, element, exact, func, node, object, oneOfType, string} from 'prop-types';
import {FormStepContext} from '../form-step';
import {ANIMATION_DURATION} from '../../../constant/Application';
import {NO_REGISTER} from '../../../constant/FormStep';

const propTypes = {
  children            : node.isRequired,
  navigateOnSelection : bool,
  name                : string.isRequired,
  props               : object,
  validation          : oneOfType([
    exact({
      isRequired   : bool,
      errorMessage : oneOfType([node, element, string]),
      validationFn : func
    }),
    arrayOf(exact({
      isRequired   : bool,
      errorMessage : oneOfType([node, element, string]),
      validationFn : func
    }))
  ])
};

const defaultProps = {
  navigateOnSelection : false,
  props               : {}
};

function FormComponent(props) {
  const {
    children,
    props : componentProps,
    name,
    navigateOnSelection,
    validation
  } = props;
  const {
    getFieldErrors,
    getFieldValue,
    handleOnGoNext,
    isStepSubmitted,
    onChange,
    registerField,
    unregisterField
  } = useContext(FormStepContext);
  const goToNextStep = useRef(handleOnGoNext);

  goToNextStep.current = handleOnGoNext;

  const handleOnChange = useCallback(value => {
    onChange(name, value);

    if (navigateOnSelection) {
      global.setTimeout(() => {
        goToNextStep.current();
      }, ANIMATION_DURATION);
    }
  }, [navigateOnSelection, name, onChange]);

  useEffect(function componentDidMount() {
    if (name !== NO_REGISTER) {
      registerField({
        defaultValue : componentProps?.defaultValue,
        name,
        validation
      });

    }

    return function componentWillUnmount() {
      if (name !== NO_REGISTER) {
        unregisterField(name);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const errors = useMemo(() => {
    if (isStepSubmitted) {
      const fieldErrors = getFieldErrors(name);

      return fieldErrors;
    }

    return [];
  }, [getFieldErrors, isStepSubmitted, name]);

  const value = useMemo(() => {
    const val = getFieldValue(name) || componentProps.defaultValue;

    return val;
  }, [componentProps.defaultValue, getFieldValue, name]);

  return React.cloneElement(children, {
    errors,
    onChange     : handleOnChange,
    onPressEnter : event => {
      if (!event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();

        handleOnGoNext();
      }
    },
    /*
      handleOnGoNext rather than the raw submit: on the submit step it reports the step
      first and then submits, so the button and the Enter key take the same path. Wired
      straight to the submit before, which is why step_index 11 never reported for anyone
      who clicked the button.
    */
    onSubmit     : handleOnGoNext,
    value
  });
}

FormComponent.propTypes = propTypes;
FormComponent.defaultProps = defaultProps;

export default FormComponent;
